import type { PagerLabels } from './pager';
import type { DataTableLabels } from './data-table';
import * as React from 'react';

/**
 * Fallback strings for design-system components whose caller passes none:
 * dialog and sheet close buttons, `LoadingState`, `ErrorState`,
 * `CopyableId` and the `CameraTile` badge. Apps provide translated ones once at the root with
 * `useComponentLabels()` from `@cguard/i18n`; the English defaults cover
 * stories and tests. A prop on the component still wins.
 */
/** Camera tile status badge words and the offline placeholder. */
export interface CameraTileLabels {
  readonly live: string;
  readonly connecting: string;
  readonly offline: string;
  readonly cameraOffline: string;
}

export interface DesignSystemLabels {
  readonly close: string;
  readonly loading: string;
  readonly error: string;
  readonly retry: string;
  readonly copy: string;
  readonly copied: string;
  readonly copyFailed: string;
  readonly camera: CameraTileLabels;
  readonly pager: PagerLabels;
  readonly table: Required<DataTableLabels>;
}

const englishLabels: DesignSystemLabels = {
  close: 'Close',
  loading: 'Loading…',
  error: 'Something went wrong',
  retry: 'Try again',
  copy: 'Copy',
  copied: 'Copied',
  copyFailed: 'Copy failed',
  pager: {
    navigation: 'Pagination', previous: 'Previous', next: 'Next',
    pageOf: (page, pageCount) => `Page ${page} of ${pageCount}`,
    page: (page) => `Page ${page}`,
  },
  table: {
    noRows: 'No rows.', navigation: 'Pagination', previous: 'Previous', next: 'Next',
    pageOf: (page, pageCount) => `Page ${page} of ${pageCount}`,
  },
  camera: {
    live: 'Live',
    connecting: 'Connecting',
    offline: 'Offline',
    cameraOffline: 'Camera offline',
  },
};

const LabelsContext = React.createContext<DesignSystemLabels>(englishLabels);

export function DesignSystemLabelsProvider({
  labels,
  children,
}: {
  readonly labels: DesignSystemLabels;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return <LabelsContext.Provider value={labels}>{children}</LabelsContext.Provider>;
}

export function useDesignSystemLabels(): DesignSystemLabels {
  return React.useContext(LabelsContext);
}
